/**
 * MetricsPaywallConversionPage — 数据中台 · 付费转化看板（ADM-MTR-S012）
 *
 * 活跃 → 进付费面板 → 创建付费订单 → 付费 四级人数，三个视图共用同一组筛选：
 * - 分组表格：任选分组维度 + 展示指标，首行为全窗口去重汇总，可排序、导出 CSV
 * - 时间趋势：按日折线，可再按一个维度拆分（取人数最多的 6 组）
 * - 转化链路：汇总四级漏斗 + 级间转化率
 * 数据走 useMetricsPaywallConversion（GET /api/v1/metrics/paywall-conversion，按 UTC 日）。
 * 应用在页内筛选（默认全部），不跟随顶栏 App；ARPU / ARPPU 依赖联合 USD 收入发布、当前按 PENDING 灰显，不出假数字。
 */

import {
  PAYWALL_CONVERSION_MAX_DAYS,
  type PaywallConversionDimension,
  SETTLEMENT_CHANNELS,
  type SettlementChannel,
  earliestPaywallConversionDay,
  isSettlementChannel,
} from '@novel-admin/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type {
  PaywallConversionMetricsView,
  PaywallConversionRowView,
  PaywallConversionViewData,
} from '~/data/adapters/metrics-paywall-conversion-adapter';
import { useMetricsNestedLevels } from '~/data/metrics-nested-levels';
import {
  fetchMetricsPaywallConversion,
  metricsPaywallConversionQueryKey,
  useMetricsPaywallConversion,
} from '~/data/queries';
import { fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { DateRangeButton } from '~/ui/DatePicker';
import { Dialog, DialogBody } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { PageTabs } from '~/ui/PageTabs';
import { Select } from '~/ui/Select';
import { ChartSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LineChartX } from '~/ui/charts';
import { MetricsAppFilterButton } from './MetricsAppFilterButton';
import {
  ChipRow,
  SERIES_COLORS,
  ToggleChip,
  daysBetween,
  defaultRange,
  downloadCsvFile,
  toDay,
} from './conversion-dashboard-shared';
import { MetricsDataTableHeading } from './metrics-data-explanation';
import {
  NESTED_PLACEHOLDER_TEXT,
  type NestedFlatEntry,
  NestedLabelCell,
  flattenNestedRows,
  nestedActiveDeeperLevels,
  nestedDeeperGroupBys,
  toggleNestedKey,
} from './metrics-nested-rows';
import {
  Cal,
  EmptyBlock,
  PENDING_REASON,
  PendTag,
  StateGate,
  type StateGateMode,
} from './metrics-shared';

type ViewTab = 'table' | 'trend' | 'chain';
type MetricKind = 'count' | 'pct' | 'duration';
type MetricKey = Exclude<keyof PaywallConversionMetricsView, 'avgPayDurationSamples'>;

type MetricDef = {
  key: MetricKey | 'arpu' | 'arppu' | 'revenueShare';
  label: string;
  group: '人数' | '其他' | '比例' | '占比';
  kind: MetricKind;
  tip: string;
  pending?: boolean;
};

/** 付费用户口径：结算通道含已登记框架通道；埋点的框架通道支付方式按 `channel_of()` 归并到该通道 */
const PAID_USERS_TIP =
  '当日有到账结算（payment_settlements）的用户，含 IAP、Stripe 与已登记框架通道（PayerMax / Airwallex）；埋点的 PAYERMAX_* 支付方式归并到 PayerMax';

/** 收入派生指标统一灰显原因（ARPU / ARPPU / 付费金额占比） */
const REVENUE_PENDING_TIP = PENDING_REASON.rev ?? '收入覆盖未就绪';

const PAID_USERS_METRIC: MetricDef = {
  key: 'paidUsers',
  label: '付费用户数',
  group: '人数',
  kind: 'count',
  tip: PAID_USERS_TIP,
};

const METRICS: MetricDef[] = [
  {
    key: 'activeUsers',
    label: '活跃用户数',
    group: '人数',
    kind: 'count',
    tip: '当日在自建埋点有任意事件的用户（仅上报同意分析采集的用户）',
  },
  {
    key: 'paywallUsers',
    label: '进付费面板用户数',
    group: '人数',
    kind: 'count',
    tip: '当日有 paywall_product_view（付费墙成功展示商品）的用户',
  },
  {
    key: 'orderUsers',
    label: '创建付费订单用户数',
    group: '人数',
    kind: 'count',
    tip: '当日有点击购买（store_purchase_attempt / Web web_purchase_attempt）的用户',
  },
  {
    key: 'paidUsers',
    label: '付费用户数',
    group: '人数',
    kind: 'count',
    tip: PAID_USERS_TIP,
  },
  {
    key: 'avgPayDurationMs',
    label: '完成付费平均耗时',
    group: '其他',
    kind: 'duration',
    tip: '点击购买 → 确认到账的平均耗时；仅应用内支付路径上报',
  },
  {
    key: 'arpu',
    label: 'ARPU',
    group: '其他',
    kind: 'count',
    tip: REVENUE_PENDING_TIP,
    pending: true,
  },
  {
    key: 'arppu',
    label: 'ARPPU',
    group: '其他',
    kind: 'count',
    tip: REVENUE_PENDING_TIP,
    pending: true,
  },
  {
    key: 'paywallRate',
    label: '进付费面板用户比例',
    group: '比例',
    kind: 'pct',
    tip: '进付费面板用户数 ÷ 活跃用户数',
  },
  {
    key: 'orderRate',
    label: '创建付费订单用户比例',
    group: '比例',
    kind: 'pct',
    tip: '创建付费订单用户数 ÷ 活跃用户数',
  },
  {
    key: 'paidRate',
    label: '付费用户比例',
    group: '比例',
    kind: 'pct',
    tip: '付费用户数 ÷ 活跃用户数',
  },
  {
    key: 'orderConversion',
    label: '创建付费订单用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '创建付费订单用户数 ÷ 进付费面板用户数',
  },
  {
    key: 'paidConversion',
    label: '付费用户转化率',
    group: '比例',
    kind: 'pct',
    tip: '付费用户数 ÷ 创建付费订单用户数',
  },
  {
    key: 'activeShare',
    label: '活跃用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行活跃用户数 ÷ 汇总行',
  },
  {
    key: 'paywallShare',
    label: '进付费面板用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行进付费面板用户数 ÷ 汇总行',
  },
  {
    key: 'orderShare',
    label: '创建付费订单用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行创建付费订单用户数 ÷ 汇总行',
  },
  {
    key: 'paidShare',
    label: '付费用户数占比',
    group: '占比',
    kind: 'pct',
    tip: '本行付费用户数 ÷ 汇总行',
  },
  {
    key: 'revenueShare',
    label: '付费金额占比',
    group: '占比',
    kind: 'pct',
    tip: REVENUE_PENDING_TIP,
    pending: true,
  },
];

/** 分母或分子依赖活跃人数的指标（按商城类型 / 支付方式拆分时不可归因） */
const ACTIVE_DEPENDENT_METRICS: MetricDef['key'][] = [
  'activeUsers',
  'paywallRate',
  'orderRate',
  'paidRate',
];
/** 依赖进付费面板人数的指标（按支付方式拆分时不可归因） */
const PAYWALL_DEPENDENT_METRICS: MetricDef['key'][] = ['paywallUsers', 'orderConversion'];

const DEFAULT_METRICS: MetricDef['key'][] = [
  'activeUsers',
  'paywallUsers',
  'paywallRate',
  'orderConversion',
  'paidConversion',
];

const DIMENSIONS: { key: PaywallConversionDimension; label: string; group: '基础' | '付费' }[] = [
  { key: 'date', label: '日期', group: '基础' },
  { key: 'app', label: '应用', group: '基础' },
  { key: 'language', label: '语言', group: '基础' },
  { key: 'channel', label: '渠道', group: '基础' },
  { key: 'platform', label: '终端', group: '基础' },
  { key: 'app_version', label: '版本', group: '基础' },
  { key: 'scenario', label: '商城类型', group: '付费' },
  { key: 'payment_channel', label: '支付方式', group: '付费' },
  { key: 'user_type', label: '用户类型', group: '付费' },
  { key: 'payer_status', label: '是否付费用户', group: '付费' },
];

/** 支付方式（结算通道）展示名；按 `SettlementChannel` 建表，闭集新增通道时编译期提示补齐 */
const PAYMENT_CHANNEL_LABEL: Record<SettlementChannel, string> = {
  IAP: 'IAP',
  STRIPE: 'Stripe',
  PAYERMAX: 'PayerMax',
  AIRWALLEX: 'Airwallex',
};

/** 支付方式筛选项 = 结算通道层闭集 `SETTLEMENT_CHANNELS`（IAP / STRIPE / 已登记框架通道，AD §14.1），与 query schema 同源 */
const PAYMENT_CHANNEL_OPTIONS = SETTLEMENT_CHANNELS.map((channel) => ({
  value: channel,
  label: PAYMENT_CHANNEL_LABEL[channel],
}));

const SCENARIO_LABEL: Record<string, string> = {
  charge_center: '充值中心',
  reading_pass: '阅读卡',
  new_user_offer: '新人特惠',
  promo_offer: '运营活动',
  insufficient_balance_offer: '余额不足优惠',
};
const PLATFORM_LABEL: Record<string, string> = { IOS: 'iOS', ANDROID: 'Android', WEB: 'Web' };
const USER_TYPE_LABEL: Record<string, string> = { new: '新用户', returning: '老用户' };
const PAYER_LABEL: Record<string, string> = { payer: '历史付费用户', non_payer: '未付费用户' };
const TREND_SERIES_LIMIT = 6;

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  // 先取整到秒再拆分，避免 119.6s 显示成 1m60s
  const totalSeconds = Math.round(ms / 1000);
  return `${Math.floor(totalSeconds / 60)}m${totalSeconds % 60}s`;
}

function formatMetric(def: MetricDef, metrics: PaywallConversionMetricsView): string {
  if (def.pending) return 'PENDING';
  const value = metrics[def.key as MetricKey];
  if (value === null) return '—';
  if (def.kind === 'pct') return `${(value * 100).toFixed(2)}%`;
  if (def.kind === 'duration') return formatDuration(value);
  return value.toLocaleString();
}

function dimensionLabel(
  dimension: PaywallConversionDimension,
  value: string | null | undefined,
  data: PaywallConversionViewData,
): string {
  if (value === null || value === undefined || value === '') return '未知';
  switch (dimension) {
    case 'app':
      return data.options.apps.find((app) => app.id === value)?.name ?? value;
    case 'language':
      return fmtLang(value);
    case 'channel':
      return value === 'organic' ? '自然量' : value;
    case 'platform':
      return PLATFORM_LABEL[value] ?? value;
    case 'scenario':
      return SCENARIO_LABEL[value] ?? value;
    case 'user_type':
      return USER_TYPE_LABEL[value] ?? value;
    case 'payer_status':
      return PAYER_LABEL[value] ?? value;
    case 'payment_channel':
      return isSettlementChannel(value) ? PAYMENT_CHANNEL_LABEL[value] : value;
    default:
      return value;
  }
}

function downloadCsv(data: PaywallConversionViewData, metrics: MetricDef[]): void {
  const header = [
    ...data.groupBy.map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d),
    ...metrics.map((m) => m.label),
  ];
  const line = (dims: string[], view: PaywallConversionMetricsView) => [
    ...dims,
    ...metrics.map((m) => formatMetric(m, view)),
  ];
  downloadCsvFile(`paywall-conversion-${data.window.from}_${data.window.to}.csv`, [
    header,
    line(
      data.groupBy.map((_, index) => (index === 0 ? '汇总' : '')),
      data.summary,
    ),
    ...data.rows.map((row) =>
      line(
        data.groupBy.map((d) => dimensionLabel(d, row.dims[d], data)),
        row,
      ),
    ),
  ]);
}

export default function MetricsPaywallConversionPage() {
  const [appIds, setAppIds] = useState<string[]>([]);
  const [tab, setTab] = useState<ViewTab>('table');
  const [range, setRange] = useState<[Date, Date]>(defaultRange);
  const [languages, setLanguages] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [appVersions, setAppVersions] = useState<string[]>([]);
  const [scenarios, setScenarios] = useState<string[]>([]);
  const [paymentChannels, setPaymentChannels] = useState<string[]>([]);
  const [userType, setUserType] = useState<'' | 'new' | 'returning'>('');
  const [payerStatus, setPayerStatus] = useState<'' | 'payer' | 'non_payer'>('');
  const [minActiveInput, setMinActiveInput] = useState('');
  const [tableDims, setTableDims] = useState<PaywallConversionDimension[]>(['date']);
  const [visibleMetrics, setVisibleMetrics] = useState<MetricDef['key'][]>(DEFAULT_METRICS);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [trendMetric, setTrendMetric] = useState<MetricKey>('paidUsers');
  const [trendSplit, setTrendSplit] = useState<PaywallConversionDimension | ''>('');

  const from = toDay(range[0]);
  const to = toDay(range[1]);
  // 接口窗口上限 92 天；日期选择器自带「近半年 / 今年」等更长快捷项，超限时不发请求、提示缩短
  const spanDays = daysBetween(from, to).length;
  const earliestDay = earliestPaywallConversionDay(new Date());
  // 超出接口窗口上限，或起始日早于埋点保留期（前三级恒 0）→ 不发请求、提示调整
  const rangeIssue =
    spanDays > PAYWALL_CONVERSION_MAX_DAYS
      ? {
          title: `时间窗最多 ${PAYWALL_CONVERSION_MAX_DAYS} 天`,
          note: `当前选择了 ${spanDays} 天，请缩短日期范围`,
        }
      : from < earliestDay
        ? {
            title: '起始日期超出埋点保留期',
            note: `埋点原始数据只保留 90 天，请从 ${earliestDay} 及之后开始选择`,
          }
        : null;
  const rangeBlocked = rangeIssue !== null;
  const minActiveUsers = /^\d+$/.test(minActiveInput.trim()) ? Number(minActiveInput.trim()) : null;
  // 分组表格只先取第一个维度，后面的维度逐级展开时再按前缀取数
  const groupBy: PaywallConversionDimension[] =
    tab === 'table'
      ? tableDims.slice(0, 1)
      : tab === 'trend' && trendSplit
        ? ['date', trendSplit]
        : ['date'];

  const minActiveApplicable = !groupBy.some(
    (dimension) => dimension === 'scenario' || dimension === 'payment_channel',
  );
  const queryBase = {
    from,
    to,
    appIds,
    languages,
    channels,
    platforms,
    appVersions,
    scenarios,
    paymentChannels,
    userType,
    payerStatus,
  };
  const { data, isLoading, isFetching, isError, refetch } = useMetricsPaywallConversion({
    ...queryBase,
    groupBy,
    // 按商城类型 / 支付方式分组时活跃人数不可归因，门槛不下发（服务端会拒绝该组合）
    minActiveUsers: minActiveApplicable ? minActiveUsers : null,
    enabled: !rangeBlocked,
  });
  const queryClient = useQueryClient();
  // 展开状态绑定当前筛选 / 分组：条件一变，旧的展开键即失效
  const expandSignature = JSON.stringify([queryBase, tableDims, minActiveUsers]);
  const [expandedState, setExpandedState] = useState<{ signature: string; keys: Set<string> }>({
    signature: '',
    keys: new Set(),
  });
  const expanded = useMemo(
    () => (expandedState.signature === expandSignature ? expandedState.keys : new Set<string>()),
    [expandedState, expandSignature],
  );
  const toggleExpanded = (key: string) =>
    setExpandedState({ signature: expandSignature, keys: toggleNestedKey(expanded, key) });
  // 父行是该前缀的真实去重聚合（跨日 / 跨语言的人数不能由子行相加）；更深级不带门槛，只有第一级过滤
  const deeperLevels = useMetricsNestedLevels({
    levelGroupBys: nestedDeeperGroupBys(tableDims),
    activeDeeperLevels: tab === 'table' ? nestedActiveDeeperLevels(expanded, tableDims.length) : 0,
    queryKeyOf: (levelGroupBy) =>
      metricsPaywallConversionQueryKey({
        ...queryBase,
        groupBy: levelGroupBy as PaywallConversionDimension[],
        minActiveUsers: null,
      }),
    fetchLevel: (levelGroupBy) =>
      fetchMetricsPaywallConversion({
        ...queryBase,
        groupBy: levelGroupBy as PaywallConversionDimension[],
        minActiveUsers: null,
      }),
    enabled: !rangeBlocked && tab === 'table',
  });

  const activeFilterCount =
    appIds.length +
    languages.length +
    channels.length +
    platforms.length +
    appVersions.length +
    scenarios.length +
    paymentChannels.length +
    (userType ? 1 : 0) +
    (payerStatus ? 1 : 0) +
    (minActiveUsers !== null ? 1 : 0);
  const resetFilters = () => {
    setAppIds([]);
    setLanguages([]);
    setChannels([]);
    setPlatforms([]);
    setAppVersions([]);
    setScenarios([]);
    setPaymentChannels([]);
    setUserType('');
    setPayerStatus('');
    setMinActiveInput('');
  };

  const shownMetrics = METRICS.filter((m) => visibleMetrics.includes(m.key));
  // 分组里有日期维度时默认按日期倒序，表头箭头与之一致
  const activeSort =
    sort ?? (tableDims.includes('date') ? { key: 'dim', dir: 'desc' as const } : null);
  // 同级行排序：点首列表头按各级自己的维度值排，点指标列按指标值排；
  // 未手动排序时，维度为日期的那一级按日期倒序，其余级保持服务端顺序
  const sortRows = (rows: PaywallConversionRowView[], depth: number) => {
    const levelDimension = tableDims[depth];
    if (!levelDimension) return rows;
    const effective =
      sort ?? (levelDimension === 'date' ? { key: 'dim', dir: 'desc' as const } : null);
    if (!effective) return rows;
    const factor = effective.dir === 'asc' ? 1 : -1;
    const sortValue = (row: PaywallConversionRowView): number | string | null =>
      effective.key === 'dim'
        ? (row.dims[levelDimension] ?? null)
        : row[effective.key as MetricKey];
    return [...rows].sort((left, right) => {
      const a = sortValue(left);
      const b = sortValue(right);
      if (a === b) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return (a < b ? -1 : 1) * factor;
    });
  };
  const flatRows: Array<NestedFlatEntry<PaywallConversionRowView>> = flattenNestedRows({
    dims: tableDims,
    levels: [
      { rows: data?.rows, isLoading, isError },
      ...deeperLevels.map((level) => ({
        rows: level.data?.rows,
        isLoading: level.isLoading,
        isError: level.isError,
      })),
    ],
    expanded,
    sortRows,
  });
  const truncatedDeeperLevel = deeperLevels.findIndex((level) => level.data?.truncated);

  // 点表头在当前生效的排序上循环：降序 → 升序 → 回到默认
  const toggleSort = (key: string) =>
    setSort(() => {
      if (activeSort?.key !== key) return { key, dir: 'desc' };
      return activeSort.dir === 'desc' ? { key, dir: 'asc' } : null;
    });

  const mode: StateGateMode = rangeBlocked
    ? 'normal'
    : isLoading
      ? 'loading'
      : isError || !data
        ? 'error'
        : 'normal';

  return (
    <div className="page" data-screen-label="付费转化看板">
      <div className="page-header">
        <div>
          <h1 className="page-title">付费转化看板</h1>
          <div className="page-sub hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            活跃 → 进付费面板 → 创建付费订单 → 付费 · 按 UTC 日去重
            <Cal t="UTC 日" title="日期按 UTC 日切；人数按用户去重，汇总行按整个时间窗去重" />
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          // 重取期间 data 仍是上一组筛选的占位数据，禁止导出以免导出与当前筛选不符
          disabled={!data || isFetching || rangeBlocked || tab !== 'table'}
          title={tab === 'table' ? '导出当前分组表格' : '切到「分组表格」后导出'}
          onClick={async () => {
            if (!data) return;
            // 表格只取到展开的层级；导出要全部维度的明细行，按完整分组再取一次（同参数命中缓存）
            const fullOptions = {
              ...queryBase,
              groupBy: tableDims,
              minActiveUsers: minActiveApplicable ? minActiveUsers : null,
            };
            try {
              const full =
                tableDims.length > 1
                  ? await queryClient.fetchQuery({
                      queryKey: metricsPaywallConversionQueryKey(fullOptions),
                      queryFn: () => fetchMetricsPaywallConversion(fullOptions),
                    })
                  : data;
              downloadCsv(full, shownMetrics);
              toast('已导出 CSV', { tone: 'pos', icon: 'IconDownload' });
            } catch {
              toast('导出失败，请重试', { tone: 'neg', icon: 'IconX' });
            }
          }}
        >
          <Icons.IconDownload size={13} />
          导出
        </button>
      </div>

      <div
        className="card"
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <span className="hstack" style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}>
          <Icons.IconFilter size={13} />
          筛选
        </span>
        <DateRangeButton value={range} onChange={setRange} />
        <MetricsAppFilterButton value={appIds} onChange={setAppIds} />
        <FilterButton
          label="终端"
          options={['IOS', 'ANDROID', 'WEB'].map((v) => ({
            value: v,
            label: PLATFORM_LABEL[v] ?? v,
          }))}
          value={platforms}
          onChange={setPlatforms}
        />
        <FilterButton
          label="语言"
          searchable
          options={(data?.options.languages ?? languages).map((v) => ({
            value: v,
            label: fmtLang(v),
          }))}
          value={languages}
          onChange={setLanguages}
        />
        <FilterButton
          label="渠道"
          searchable
          options={(data?.options.channels ?? channels).map((v) => ({
            value: v,
            label: v === 'organic' ? '自然量' : v,
          }))}
          value={channels}
          onChange={setChannels}
        />
        <FilterButton
          label="版本"
          searchable
          options={data?.options.app_versions ?? appVersions}
          value={appVersions}
          onChange={setAppVersions}
        />
        <Select
          value={userType}
          onChange={(e) => setUserType(e.target.value as typeof userType)}
          style={{ width: 110 }}
        >
          <option value="">全部用户类型</option>
          <option value="new">新用户</option>
          <option value="returning">老用户</option>
        </Select>
        <Select
          value={payerStatus}
          onChange={(e) => setPayerStatus(e.target.value as typeof payerStatus)}
          style={{ width: 130 }}
        >
          <option value="">全部付费状态</option>
          <option value="payer">历史付费用户</option>
          <option value="non_payer">未付费用户</option>
        </Select>
        <FilterButton
          label="商城类型"
          options={(data?.options.scenarios ?? scenarios).map((v) => ({
            value: v,
            label: SCENARIO_LABEL[v] ?? v,
          }))}
          value={scenarios}
          onChange={setScenarios}
        />
        <FilterButton
          label="支付方式"
          options={PAYMENT_CHANNEL_OPTIONS}
          value={paymentChannels}
          onChange={setPaymentChannels}
        />
        <label
          className="hstack"
          style={{ gap: 6, fontSize: 12, color: 'var(--fg-muted)' }}
          title="只显示活跃用户数不低于该值的分组行（不影响汇总行）"
        >
          活跃人数 ≥
          <input
            className="input mono"
            inputMode="numeric"
            placeholder={minActiveApplicable ? '不限' : '不适用'}
            disabled={!minActiveApplicable}
            title={
              minActiveApplicable
                ? undefined
                : '按商城类型 / 支付方式分组时活跃人数不可归因，门槛不生效'
            }
            value={minActiveInput}
            onChange={(e) => setMinActiveInput(e.target.value.replace(/[^\d]/g, ''))}
            style={{ width: 80 }}
          />
        </label>
        <div className="hstack" style={{ gap: 8, marginLeft: 'auto' }}>
          {activeFilterCount > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={resetFilters}>
              <Icons.IconX size={11} />
              重置 {activeFilterCount}
            </button>
          )}
          {isFetching && !isLoading && (
            <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>更新中…</span>
          )}
        </div>
      </div>

      <PageTabs
        label="视图"
        value={tab}
        items={[
          { id: 'table', label: '分组表格' },
          { id: 'trend', label: '时间趋势' },
          { id: 'chain', label: '转化链路' },
        ]}
        onChange={setTab}
      />

      {tab === 'table' && (
        <div
          className="card"
          style={{
            padding: '12px 14px',
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 1fr) minmax(0, 1fr)',
            gap: '10px 24px',
          }}
        >
          <section className="vstack" style={{ gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>分组维度</div>
            {(['基础', '付费'] as const).map((group) => (
              <ChipRow key={group} label={group}>
                {DIMENSIONS.filter((d) => d.group === group).map((d) => (
                  <ToggleChip
                    key={d.key}
                    label={d.label}
                    on={tableDims.includes(d.key)}
                    onClick={() =>
                      setTableDims((current) =>
                        current.includes(d.key)
                          ? // 至少保留一个分组维度：空 group_by 在服务端回落为按日期，芯片状态会与表格不一致
                            current.length === 1
                            ? current
                            : current.filter((x) => x !== d.key)
                          : // 按点击顺序追加：第一个选的是顶层，后选的逐级嵌套在它下面
                            [...current, d.key],
                      )
                    }
                  />
                ))}
              </ChipRow>
            ))}
          </section>
          <section className="vstack" style={{ gap: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>展示指标</div>
            {(['人数', '其他', '比例', '占比'] as const).map((group) => (
              <ChipRow key={group} label={group}>
                {METRICS.filter((m) => m.group === group).map((m) => (
                  <ToggleChip
                    key={m.key}
                    label={m.label}
                    title={m.tip}
                    disabled={m.pending}
                    on={visibleMetrics.includes(m.key)}
                    onClick={() =>
                      setVisibleMetrics((current) =>
                        current.includes(m.key)
                          ? current.filter((x) => x !== m.key)
                          : [...current, m.key],
                      )
                    }
                  />
                ))}
              </ChipRow>
            ))}
          </section>
          {(tableDims.includes('scenario') || tableDims.includes('payment_channel')) && (
            <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: 'var(--fg-faint)' }}>
              按商城类型 /
              支付方式分组时，活跃用户数无法归属到具体商城或支付方式（支付方式下进付费面板人数同理），对应列显示「—」，只在汇总行展示。
              {tableDims.includes('payment_channel') &&
                (data?.zeroFillSkipped === 'truncated'
                  ? '未带支付方式的记录归入「未知」；结果已截断，未出现的支付方式不补 0（可能只是被截掉）。'
                  : data?.zeroFillSkipped === 'row_limit'
                    ? '未带支付方式的记录归入「未知」；补齐会超过行数上限，未出现的支付方式不再逐行补 0（缺席即为 0）。'
                    : '未带支付方式的记录归入「未知」；所选范围内没有数据的支付方式显示 0，不隐藏。')}
            </div>
          )}
        </div>
      )}

      <StateGate
        mode={mode}
        refreshing={isFetching}
        skeleton={tab === 'table' ? <TableSkeleton rows={10} cols={8} /> : <ChartSkeleton />}
        errorNote="GET /api/v1/metrics/paywall-conversion 加载失败 · 请重试"
        onRetry={refetch}
      >
        {rangeIssue && <EmptyBlock title={rangeIssue.title} note={rangeIssue.note} />}
        {!rangeBlocked && data && tab === 'table' && (
          <GroupedTable
            data={data}
            dims={tableDims}
            entries={flatRows}
            truncatedDeeperLevel={truncatedDeeperLevel}
            onToggle={toggleExpanded}
            metrics={shownMetrics}
            sort={activeSort}
            onSort={toggleSort}
          />
        )}
        {!rangeBlocked && data && tab === 'trend' && (
          <TrendView
            data={data}
            metric={trendMetric}
            setMetric={setTrendMetric}
            split={trendSplit}
            setSplit={setTrendSplit}
          />
        )}
        {!rangeBlocked && data && tab === 'chain' && <ChainView data={data} />}
      </StateGate>
    </div>
  );
}

function GroupedTable({
  data,
  dims,
  entries,
  truncatedDeeperLevel,
  onToggle,
  metrics,
  sort,
  onSort,
}: {
  data: PaywallConversionViewData;
  dims: PaywallConversionDimension[];
  entries: Array<NestedFlatEntry<PaywallConversionRowView>>;
  /** 第几个更深级被服务端截断（-1 = 都没截断） */
  truncatedDeeperLevel: number;
  onToggle: (key: string) => void;
  metrics: MetricDef[];
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
}) {
  const [explainedMetric, setExplainedMetric] = useState<MetricDef | null>(null);
  const arrow = (key: string) => (sort?.key === key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : '');
  const dimensionHead = dims
    .map((d) => DIMENSIONS.find((x) => x.key === d)?.label ?? d)
    .join(' › ');
  const rowCount = entries.filter((entry) => entry.kind === 'row').length;
  const getMetricFormula = (metric: MetricDef) => {
    if (metric.pending || metric.kind !== 'pct') return null;
    if (metric.group === '占比') {
      const baseLabel = metric.label.replace('占比', '');
      return `本行${baseLabel} ÷ 当前筛选汇总行${baseLabel} × 100%`;
    }
    return `${metric.tip} × 100%`;
  };
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <MetricsDataTableHeading title="付费转化明细" report="paywall-conversion" />
      {(data.truncated || truncatedDeeperLevel >= 0) && (
        <div
          style={{
            padding: '8px 14px',
            fontSize: 12,
            color: 'var(--fg-muted)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {data.truncated
            ? `分组行超过上限，仅展示前 ${data.rows.length.toLocaleString()} 行；请缩小时间窗。`
            : `第 ${truncatedDeeperLevel + 2} 级明细行超过上限，部分父行的下一级明细不全；请缩小时间窗。`}
        </div>
      )}
      {dims.length > 1 && (
        <div style={{ padding: '6px 14px', fontSize: 12, color: 'var(--fg-muted)' }}>
          按「{dimensionHead}」逐级嵌套，点行首箭头展开下一级。
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <Table className="tbl" style={{ tableLayout: 'auto', minWidth: 720 }}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => onSort('dim')}>
                {dimensionHead}
                {arrow('dim')}
              </th>
              {metrics.map((m) => (
                <th
                  key={m.key}
                  className="num"
                  style={{ cursor: m.pending ? 'default' : 'pointer', whiteSpace: 'nowrap' }}
                  onClick={() => !m.pending && onSort(m.key)}
                >
                  {m.label}
                  <button
                    type="button"
                    aria-label={`查看${m.label}的数据取值逻辑`}
                    aria-haspopup="dialog"
                    aria-expanded={explainedMetric?.key === m.key}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExplainedMetric(m);
                    }}
                    style={{
                      marginLeft: 4,
                      padding: 0,
                      width: 16,
                      height: 16,
                      border: '1px solid var(--fg-faint)',
                      borderRadius: '50%',
                      background: 'transparent',
                      color: 'var(--fg-muted)',
                      fontSize: 11,
                      lineHeight: '14px',
                      cursor: 'pointer',
                      verticalAlign: 'middle',
                    }}
                  >
                    ?
                  </button>
                  {m.pending && <PendTag k="rev" label="P·收入" />}
                  {arrow(m.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr style={{ fontWeight: 600, background: 'var(--bg-subtle)' }}>
              <td>汇总</td>
              {metrics.map((m) => (
                <td key={m.key} className="num mono">
                  {formatMetric(m, data.summary)}
                </td>
              ))}
            </tr>
            {entries.map((entry) => {
              if (entry.kind === 'placeholder')
                return (
                  <tr key={entry.key} style={{ background: 'var(--bg-subtle)' }}>
                    <td
                      colSpan={metrics.length + 1}
                      style={{
                        paddingLeft: 14 + entry.depth * 20 + 26,
                        fontSize: 12,
                        color: 'var(--fg-muted)',
                      }}
                    >
                      {NESTED_PLACEHOLDER_TEXT[entry.state]}
                    </td>
                  </tr>
                );
              const dimension = dims[entry.depth] as PaywallConversionDimension;
              const label = dimensionLabel(dimension, entry.row.dims[dimension], data);
              return (
                <tr
                  key={entry.key}
                  style={entry.depth > 0 ? { background: 'var(--bg-subtle)' } : undefined}
                >
                  <td
                    className={
                      dimension === 'date' || dimension === 'app_version' ? 'mono' : undefined
                    }
                  >
                    <NestedLabelCell
                      depth={entry.depth}
                      expandable={entry.expandable}
                      open={entry.open}
                      onToggle={() => onToggle(entry.key)}
                      toggleLabel={`${entry.open ? '折叠' : '展开'} ${label} 的下一级明细`}
                    >
                      {label}
                    </NestedLabelCell>
                  </td>
                  {metrics.map((m) => (
                    <td
                      key={m.key}
                      className="num mono"
                      style={{ color: m.pending ? 'var(--fg-faint)' : undefined }}
                    >
                      {formatMetric(m, entry.row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {explainedMetric && (
        <Dialog
          title={`${explainedMetric.label} · 数据取值逻辑`}
          subtitle="查看当前列数值的统计口径和计算方式。"
          icon="IconInfo"
          width={480}
          onClose={() => setExplainedMetric(null)}
          footer={
            <button type="button" className="btn" onClick={() => setExplainedMetric(null)}>
              知道了
            </button>
          }
        >
          <DialogBody gap={12}>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.7 }}>{explainedMetric.tip}</p>
            {getMetricFormula(explainedMetric) && (
              <div className="card-flat" style={{ padding: 12 }}>
                <div style={{ marginBottom: 5, color: 'var(--fg-muted)', fontSize: 11.5 }}>
                  计算公式
                </div>
                <p className="mono" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.7 }}>
                  {getMetricFormula(explainedMetric)}
                </p>
              </div>
            )}
            {explainedMetric.kind === 'pct' && !explainedMetric.pending && (
              <p style={{ margin: 0, color: 'var(--fg-muted)', fontSize: 12, lineHeight: 1.6 }}>
                分母为 0 或当前分组不可归因时显示「—」。
              </p>
            )}
          </DialogBody>
        </Dialog>
      )}
      {data.groupBy.length > 0 && rowCount === 0 && (
        <EmptyBlock title="当前筛选下没有分组数据" note="换个时间窗、放宽筛选或调低活跃人数门槛" />
      )}
    </div>
  );
}

function TrendView({
  data,
  metric,
  setMetric,
  split,
  setSplit,
}: {
  data: PaywallConversionViewData;
  metric: MetricKey;
  setMetric: (metric: MetricKey) => void;
  split: PaywallConversionDimension | '';
  setSplit: (split: PaywallConversionDimension | '') => void;
}) {
  // 按商城类型 / 支付方式拆分时，依赖活跃（及支付方式下的进面板）人数的指标不可归因，不出现在可选项里
  const blocked = new Set<MetricDef['key']>(
    split === 'scenario'
      ? ACTIVE_DEPENDENT_METRICS
      : split === 'payment_channel'
        ? [...ACTIVE_DEPENDENT_METRICS, ...PAYWALL_DEPENDENT_METRICS]
        : [],
  );
  const metricOptions = METRICS.filter(
    (m) => !m.pending && m.group !== '占比' && !blocked.has(m.key),
  );
  const effectiveMetric: MetricKey = blocked.has(metric) ? 'orderUsers' : metric;
  const def = METRICS.find((m) => m.key === effectiveMetric) ?? PAID_USERS_METRIC;
  const days = daysBetween(data.window.from, data.window.to);
  const series = useMemo(() => {
    const groups = new Map<string, Map<string, PaywallConversionRowView>>();
    for (const row of data.rows) {
      const group = split ? (row.dims[split] ?? '') : '';
      const byDay = groups.get(group) ?? new Map<string, PaywallConversionRowView>();
      byDay.set(row.dims.date ?? '', row);
      groups.set(group, byDay);
    }
    // 拆分组按区间内付费链路规模排序，取前 N 组避免折线过密
    const ranked = [...groups.entries()]
      .map(([group, byDay]) => ({
        group,
        byDay,
        weight: [...byDay.values()].reduce(
          (sum, row) => sum + (row.activeUsers ?? 0) + row.orderUsers,
          0,
        ),
      }))
      .sort((left, right) => right.weight - left.weight)
      .slice(0, TREND_SERIES_LIMIT);
    return ranked.map(({ group, byDay }, index) => ({
      label: split ? dimensionLabel(split, group || null, data) : def.label,
      color: SERIES_COLORS[index % SERIES_COLORS.length] ?? 'var(--chart-1)',
      values: days.map((day) => {
        const row = byDay.get(day);
        if (!row) return def.kind === 'count' ? 0 : null;
        const value = row[effectiveMetric];
        return value === null ? null : def.kind === 'pct' ? value * 100 : value;
      }),
    }));
  }, [data, split, effectiveMetric, def, days]);

  const yFormat = (value: number) =>
    def.kind === 'pct'
      ? `${value.toFixed(1)}%`
      : def.kind === 'duration'
        ? formatDuration(Math.round(value))
        : Math.round(value).toLocaleString();

  return (
    <div className="card vstack" style={{ padding: '12px 14px', gap: 10 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>指标</span>
        <Select
          value={effectiveMetric}
          onChange={(e) => setMetric(e.target.value as MetricKey)}
          style={{ width: 200 }}
        >
          {metricOptions.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 12, color: 'var(--fg-muted)', marginLeft: 8 }}>拆分</span>
        <Select
          value={split}
          onChange={(e) => setSplit(e.target.value as PaywallConversionDimension | '')}
          style={{ width: 140 }}
        >
          <option value="">不拆分</option>
          {DIMENSIONS.filter((d) => d.key !== 'date').map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </Select>
        {split && (
          <span style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
            按区间规模取前 {TREND_SERIES_LIMIT} 组
          </span>
        )}
      </div>
      {data.truncated ? (
        <EmptyBlock
          title="拆分后的分组超过上限，趋势不完整"
          note="服务端只返回了部分分组行，按此作图会漏掉组或把缺失日画成 0；请缩短时间窗或换一个取值更少的拆分维度"
        />
      ) : series.length === 0 || series.every((s) => s.values.every((v) => v === null)) ? (
        <EmptyBlock title="当前指标在所选拆分下无数据" note={def.tip} />
      ) : (
        <LineChartX
          series={series}
          xLabels={days.map((day) => day.slice(5))}
          height={300}
          yFormat={yFormat}
        />
      )}
    </div>
  );
}

/**
 * 转化链路：四级嵌套（同一 UTC 日内到过前面各级才计入后一级），人数逐级不增。
 * 条宽按四级最大值归一、级间转化率走 adapter 的零安全比率，
 * 不用通用 Funnel（它按上一级做除数，遇 0 会出 NaN / Infinity）。
 */
function ChainView({ data }: { data: PaywallConversionViewData }) {
  const summary = data.summary;
  const steps: { label: string; value: number; conversion: number | null; hint: string }[] = [
    { label: '活跃用户', value: summary.activeUsers ?? 0, conversion: null, hint: '' },
    {
      label: '进付费面板',
      value: summary.paywallUsers ?? 0,
      conversion: summary.paywallRate,
      hint: '÷ 活跃',
    },
    {
      label: '创建付费订单',
      value: summary.orderUsers,
      conversion: summary.orderConversion,
      hint: '÷ 进面板',
    },
    { label: '付费', value: summary.paidUsers, conversion: summary.paidConversion, hint: '÷ 下单' },
  ];
  const widest = Math.max(...steps.map((step) => step.value), 1);
  const pct = (value: number | null) => (value === null ? '—' : `${(value * 100).toFixed(2)}%`);
  const empty = steps.every((step) => step.value === 0);
  return (
    <div className="card vstack" style={{ padding: '14px 16px', gap: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
        {data.window.from} ~ {data.window.to} 全窗口去重人数（UTC 日）
      </div>
      {empty ? (
        <EmptyBlock title="时间窗内没有转化数据" note="换个时间窗或放宽筛选" />
      ) : (
        <div className="vstack" style={{ gap: 10 }}>
          {steps.map((step, index) => (
            <div key={step.label} className="vstack" style={{ gap: 4 }}>
              <div className="hstack" style={{ justifyContent: 'space-between', fontSize: 12.5 }}>
                <span>{step.label}</span>
                <span className="mono">
                  {step.value.toLocaleString()}
                  {index > 0 && (
                    <span style={{ color: 'var(--fg-muted)' }}>
                      {' '}
                      · {pct(step.conversion)} {step.hint}
                    </span>
                  )}
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: 'var(--bg-inset)' }}>
                <div
                  style={{
                    width: `${(step.value / widest) * 100}%`,
                    height: '100%',
                    borderRadius: 5,
                    background: SERIES_COLORS[index] ?? 'var(--chart-1)',
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="hstack" style={{ gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
        <span>
          整体付费率 <b className="mono">{pct(summary.paidRate)}</b>
        </span>
        <span title="仅应用内支付路径上报">
          完成付费平均耗时 <b className="mono">{formatDuration(summary.avgPayDurationMs)}</b>
          <span style={{ color: 'var(--fg-faint)' }}> · {summary.avgPayDurationSamples} 样本</span>
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>
        活跃 / 进面板 /
        各级嵌套：用户当日到过前面各级才计入后一级；没有对应埋点的到账用户不计入付费人数。 ARPU /
        ARPPU：{data.revenuePendingReason}。
      </div>
    </div>
  );
}
